/**
 * Product Context Manager
 * 
 * Manages global product state and provides context for actions like add to cart
 * Handles product page context, product tile hover states, and variant selection
 */

class ProductContextManager {
    constructor() {
        this.currentProduct = null;
        this.currentVariant = null;
        this.hoveredProduct = null;
        this.hoveredVariant = null;
        this.productPageContext = null;
        this.listeners = new Set();

        this.init();
    }

    init() {
        // console.log('ProductContextManager: Initializing...');
        
        // Initialize product page context if we're on a product page
        this.initProductPageContext();
        
        // Set up event listeners for product interactions
        this.setupEventListeners();
        
        // Set up mutation observer for dynamic content
        this.setupMutationObserver();
        
        // console.log('ProductContextManager: Initialized', this.getDebugInfo());
    }

    /**
     * Initialize product page context from DOM
     */
    initProductPageContext() {
        // Look for product data in block-builder-product sections
        const productSections = document.querySelectorAll('[data-handle][data-id]');
        
        if (productSections.length > 0) {
            const section = productSections[0]; // Use first product section
            const productData = {
                id: section.dataset.id,
                handle: section.dataset.handle,
                url: section.dataset.url
            };

            // Get variants from JSON script
            const variantsScript = section.querySelector('script[data-prodify-variants-json]');
            let variants = [];
            
            if (variantsScript) {
                try {
                    variants = JSON.parse(variantsScript.textContent);
                } catch (e) {
                    console.warn('Failed to parse product variants:', e);
                }
            }

            // Get selected variant from hidden span or use first variant
            const selectedVariantSpan = section.querySelector('span[style*="position: absolute"]');
            let selectedVariantId = null;
            
            if (selectedVariantSpan && selectedVariantSpan.textContent.trim()) {
                selectedVariantId = selectedVariantSpan.textContent.trim();
            } else if (variants.length > 0) {
                selectedVariantId = variants[0].id;
            }

            this.productPageContext = {
                product: productData,
                variants: variants,
                selectedVariantId: selectedVariantId
            };

            // Set current context to product page
            this.setCurrentProduct(productData, selectedVariantId);
        }
    }

    /**
     * Set up event listeners for product interactions
     */
    setupEventListeners() {
        // Listen for product tile hover events
        document.addEventListener('mouseenter', this.handleProductHover.bind(this), true);
        document.addEventListener('mouseleave', this.handleProductLeave.bind(this), true);
        
        // Listen for variant selection changes
        document.addEventListener('change', this.handleVariantChange.bind(this), true);
        document.addEventListener('click', this.handleVariantClick.bind(this), true);
        
        // Listen for custom product context events
        window.addEventListener('arc:product:setContext', this.handleSetContext.bind(this));
        window.addEventListener('arc:product:setVariant', this.handleSetVariant.bind(this));
    }

    /**
     * Set up mutation observer for dynamically loaded product content
     */
    setupMutationObserver() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if new product sections were added
                        if (node.matches && node.matches('[data-handle][data-id]')) {
                            this.handleNewProductSection(node);
                        }
                        
                        // Check for product tiles
                        if (node.querySelectorAll) {
                            const productTiles = node.querySelectorAll('.product-tile, [data-product-id]');
                            productTiles.forEach(tile => this.setupProductTile(tile));
                        }
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Handle product hover (for product tiles/cards)
     */
    handleProductHover(event) {
        // Check if event.target has the closest method (it's an Element)
        if (!event.target || typeof event.target.closest !== 'function') {
            return;
        }

        const productElement = event.target.closest('.product-tile, [data-product-id], .product-card');
        
        if (productElement) {
            const productId = productElement.dataset.productId || 
                             productElement.dataset.id ||
                             productElement.querySelector('[data-product-id]')?.dataset.productId;
            
            const variantId = productElement.dataset.variantId ||
                             productElement.dataset.selectedVariant ||
                             productElement.querySelector('[data-variant-id]')?.dataset.variantId;

            if (productId) {
                this.setHoveredProduct({
                    id: productId,
                    handle: productElement.dataset.handle,
                    url: productElement.dataset.url
                }, variantId);
            }
        }
    }

    /**
     * Handle product leave (clear hover state)
     */
    handleProductLeave(event) {
        // Check if event.target has the closest method (it's an Element)
        if (!event.target || typeof event.target.closest !== 'function') {
            return;
        }

        const productElement = event.target.closest('.product-tile, [data-product-id], .product-card');
        
        if (productElement) {
            this.clearHoveredProduct();
        }
    }

    /**
     * Handle variant selection changes
     */
    handleVariantChange(event) {
        // Check if event.target has the matches method (it's an Element)
        if (!event.target || typeof event.target.matches !== 'function') {
            return;
        }

        if (event.target.matches('select[name*="variant"], select[name*="id"]')) {
            const variantId = event.target.value;
            this.setCurrentVariant(variantId);
        }
    }

    /**
     * Handle variant click events (radio buttons, swatches)
     */
    handleVariantClick(event) {
        // Check if event.target has the matches method (it's an Element)
        if (!event.target || typeof event.target.matches !== 'function') {
            return;
        }

        if (event.target.matches('input[name*="variant"], input[name*="id"], [data-variant-id]')) {
            const variantId = event.target.value || event.target.dataset.variantId;
            if (variantId) {
                this.setCurrentVariant(variantId);
            }
        }
    }

    /**
     * Handle custom set context events
     */
    handleSetContext(event) {
        const { product, variantId } = event.detail;
        this.setCurrentProduct(product, variantId);
    }

    /**
     * Handle custom set variant events
     */
    handleSetVariant(event) {
        const { variantId } = event.detail;
        this.setCurrentVariant(variantId);
    }

    /**
     * Handle new product sections added dynamically
     */
    handleNewProductSection(section) {
        // Re-initialize product page context if this is a product section
        if (!this.productPageContext) {
            this.initProductPageContext();
        }
    }

    /**
     * Set up product tile for interaction tracking
     */
    setupProductTile(tile) {
        // Add data attributes if not present
        if (!tile.dataset.productTracked) {
            tile.dataset.productTracked = 'true';
        }
    }

    /**
     * Set current product context
     */
    setCurrentProduct(product, variantId = null) {
        this.currentProduct = product;
        this.currentVariant = variantId;
        this.notifyListeners('current', { product, variantId });
    }

    /**
     * Set current variant
     */
    setCurrentVariant(variantId) {
        this.currentVariant = variantId;
        this.notifyListeners('variant', { variantId });
    }

    /**
     * Set hovered product context
     */
    setHoveredProduct(product, variantId = null) {
        this.hoveredProduct = product;
        this.hoveredVariant = variantId;
        this.notifyListeners('hover', { product, variantId });
    }

    /**
     * Clear hovered product context
     */
    clearHoveredProduct() {
        this.hoveredProduct = null;
        this.hoveredVariant = null;
        this.notifyListeners('hover_clear', {});
    }

    /**
     * Get the most relevant product context for actions
     * Priority: hovered product > current product > product page context
     */
    getActionContext() {
        // If hovering over a product (like in a product grid), use that
        if (this.hoveredProduct) {
            return {
                product: this.hoveredProduct,
                variantId: this.hoveredVariant || this.getFirstVariantId(this.hoveredProduct.id)
            };
        }

        // If we have current product context (selected on page), use that
        if (this.currentProduct) {
            return {
                product: this.currentProduct,
                variantId: this.currentVariant || this.getFirstVariantId(this.currentProduct.id)
            };
        }

        // Fall back to product page context
        if (this.productPageContext) {
            return {
                product: this.productPageContext.product,
                variantId: this.productPageContext.selectedVariantId
            };
        }

        return null;
    }

    /**
     * Get first available variant ID for a product
     */
    getFirstVariantId(productId) {
        if (this.productPageContext && this.productPageContext.product.id === productId) {
            const variants = this.productPageContext.variants;
            return variants.length > 0 ? variants[0].id : null;
        }

        // Try to find variant data in DOM for this product
        const productElements = document.querySelectorAll(`[data-id="${productId}"]`);
        for (const element of productElements) {
            const variantsScript = element.querySelector('script[data-prodify-variants-json]');
            if (variantsScript) {
                try {
                    const variants = JSON.parse(variantsScript.textContent);
                    return variants.length > 0 ? variants[0].id : null;
                } catch (e) {
                    continue;
                }
            }
        }

        return null;
    }

    /**
     * Add a listener for context changes
     */
    addListener(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    /**
     * Notify all listeners of context changes
     */
    notifyListeners(type, data) {
        this.listeners.forEach(callback => {
            try {
                callback(type, data);
            } catch (e) {
                console.error('Error in product context listener:', e);
            }
        });
    }

    /**
     * Get debug information
     */
    getDebugInfo() {
        return {
            currentProduct: this.currentProduct,
            currentVariant: this.currentVariant,
            hoveredProduct: this.hoveredProduct,
            hoveredVariant: this.hoveredVariant,
            productPageContext: this.productPageContext,
            actionContext: this.getActionContext()
        };
    }
}

// Create global instance when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.ProductContextManager = new ProductContextManager();
    });
} else {
    // DOM is already loaded
    window.ProductContextManager = new ProductContextManager();
}

// Helper function to get current product context for actions
window.getProductContext = () => {
    try {
        return window.ProductContextManager?.getActionContext() || null;
    } catch (error) {
        console.error('Error getting product context:', error);
        return null;
    }
};

// Helper functions for manual context setting
window.setProductContext = (product, variantId) => {
    window.dispatchEvent(new CustomEvent('arc:product:setContext', {
        detail: { product, variantId }
    }));
};

window.setVariantContext = (variantId) => {
    window.dispatchEvent(new CustomEvent('arc:product:setVariant', {
        detail: { variantId }
    }));
};

export default ProductContextManager;
